import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { UNSAFE_DataRouterContext, UNSAFE_DataRouterNavigationContext } from 'react-router';

/**
 * メニューを押した直後の「移動先」を、すぐ(緊急に)覚えておく。
 * React Router は画面の更新を transition で行うため、移動先のページが重い(または lazy で読み込み中)あいだは
 * 古いページと古い強調が残り、押しても反応しないように見える。ここで覚えた移動先は、URL が実際に変わった時点で消える。
 *
 * 移動が実際には行われなかったとき(ブロックされた・別の場所へ上書きされた)は、移動先を捨てて元の強調に戻す。
 * 判定はルーターの状態(transition を待たない)で行い、クリックの時点(ルーターが移動を始める前)から見張る:
 * 待機が終わった(idle)のに現在地が移動先でなければ捨てる。
 * データルーターがない環境(MemoryRouter だけのテスト)では、URL が変わるまで保持する。
 */
export const usePendingNav = (pathname: string) => {
  const [pendingTo, setPending] = useState<string | null>(null);
  const [seenPath, setSeenPath] = useState(pathname);
  if (seenPath !== pathname) {
    setSeenPath(pathname);
    setPending(null);
  }

  const router = useContext(UNSAFE_DataRouterContext)?.router;
  const stopWatching = useRef<() => void>(() => undefined);
  useEffect(
    () => () => {
      stopWatching.current();
    },
    [],
  );

  const setPendingTo = useCallback(
    (to: string) => {
      stopWatching.current();
      setPending(to);
      if (router === undefined) return;
      const stop = router.subscribe((state) => {
        if (state.navigation.state !== 'idle') return;
        if (state.location.pathname !== to) setPending(null);
        stop();
      });
      stopWatching.current = stop;
    },
    [router],
  );

  // lazy ページなどの読み込み中(データルーターがなければ常に false)
  const navigating = useContext(UNSAFE_DataRouterNavigationContext)?.navigation.state === 'loading';

  return { pendingTo, setPendingTo, navigating };
};
